import React from "react";
import useService from "../../../hooks/useService";
import { useQuery } from "react-query";
import MaterialTable from "material-table";
import CopyIcon from "@material-ui/icons/FileCopy";
import { copyToClipboard, exportCsv } from "../../../utils";
import { useApp } from "../../../AppProvider";
import { findRawRecords } from "../../../services/crudService";

import {
  Link,
  Divider as MuiDivider,
  Breadcrumbs as MuiBreadcrumbs,
  Typography as MuiTypography,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";

import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import Panel from "../../../components/panels/Panel";
import { Helmet } from "react-helmet-async";
import { NavLink } from "react-router-dom";

const Divider = styled(MuiDivider)(spacing);
const Typography = styled(MuiTypography)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)}px);
  max-height: calc(100% - 48px);
`;

const columns = [
  { title: "Year", field: "dmr_year" },
  { title: "Month", field: "month_abbrev" },
  {
    title: "River Daily Max (DM)",
    field: "river_dmr",
    cellStyle: { borderLeft: "2px solid #e0e0e0" },
  },
  { title: "River Max Weekly (MWAT)", field: "river_mwat" },
  {
    title: "WWTP Daily Max (DM)",
    field: "wwtp_dmr",
    cellStyle: { borderLeft: "2px solid #e0e0e0" },
  },
  { title: "WWTP Max Weekly (MWAT)", field: "wwtp_mwat" },
  {
    title: "Notes",
    field: "remark",
    cellStyle: { borderLeft: "2px solid #e0e0e0" },
  },
];

const stationStyle = {
  backgroundColor: "#e0e0e0",
  fontWeight: 700,
  textAlign: "center",
  fontSize: "0.8rem",
  padding: "8px 16px",
  whiteSpace: "nowrap",
};

const subHeaderStyle = {
  backgroundColor: "#f5f5f5",
  fontWeight: 600,
  fontSize: "0.75rem",
  padding: "8px 12px",
  lineHeight: 1.3,
};

const emptyStyle = {
  backgroundColor: "#f5f5f5",
  padding: 0,
  borderBottom: "none",
};

const GroupedHeader = () => (
  <TableHead>
    <TableRow>
      <TableCell style={emptyStyle} />
      <TableCell style={emptyStyle} />
      <TableCell
        colSpan={2}
        style={{ ...stationStyle, borderLeft: "2px solid #ccc" }}
      >
        San Miguel @ Telluride
      </TableCell>
      <TableCell
        colSpan={2}
        style={{ ...stationStyle, borderLeft: "2px solid #ccc" }}
      >
        Telluride WWTP Outflow Weir
      </TableCell>
      <TableCell style={{ ...emptyStyle, borderLeft: "2px solid #ccc" }} />
    </TableRow>
    <TableRow>
      <TableCell style={subHeaderStyle}>Year</TableCell>
      <TableCell style={subHeaderStyle}>Month</TableCell>
      <TableCell style={{ ...subHeaderStyle, borderLeft: "2px solid #e0e0e0" }}>
        Daily Max (DM)
      </TableCell>
      <TableCell style={subHeaderStyle}>Max Weekly (MWAT)</TableCell>
      <TableCell style={{ ...subHeaderStyle, borderLeft: "2px solid #e0e0e0" }}>
        Daily Max (DM)
      </TableCell>
      <TableCell style={subHeaderStyle}>Max Weekly (MWAT)</TableCell>
      <TableCell style={{ ...subHeaderStyle, borderLeft: "2px solid #e0e0e0" }}>
        Notes
      </TableCell>
    </TableRow>
  </TableHead>
);

const DischargeMonitoringReport = () => {
  const service = useService({ toast: false });
  const { doToast } = useApp();

  const { data, isLoading, error } = useQuery(
    ["DischargeMonitoringReportExts"],
    async () => {
      try {
        return await service([
          findRawRecords,
          ["DischargeMonitoringReportExts"],
        ]);
      } catch (err) {
        console.error(err);
      }
    },
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  if (error) return "An error has occurred: " + error.message;

  const sortedData = data
    ? [...data].sort((a, b) => {
        if (b.dmr_year !== a.dmr_year) return b.dmr_year - a.dmr_year;
        return b.month_ndx - a.month_ndx;
      })
    : [];

  return (
    <>
      <Helmet title="Discharge Monitoring Report" />
      <Typography variant="h3" gutterBottom display="inline">
        Discharge Monitoring Report
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to="/dashboard">
          Dashboard
        </Link>
        <Typography>Discharge Monitoring Report</Typography>
      </Breadcrumbs>
      <Divider my={6} />
      <Panel>
        <TableWrapper>
          <MaterialTable
            columns={columns}
            data={sortedData}
            isLoading={isLoading}
            components={{
              Container: (props) => <div {...props} />,
              Header: () => <GroupedHeader />,
            }}
            actions={[
              {
                icon: "save_alt",
                tooltip: "Export CSV",
                isFreeAction: true,
                onClick: () => exportCsv(columns, sortedData),
              },
              {
                icon: CopyIcon,
                tooltip: "Copy Data",
                isFreeAction: true,
                onClick: () => {
                  try {
                    copyToClipboard(sortedData, columns, () =>
                      doToast("success", "Data was copied to your clipboard.")
                    );
                  } catch (error) {
                    const message = error?.message ?? "Something went wrong";
                    doToast("error", message);
                  }
                },
              },
            ]}
            options={{
              emptyRowsWhenPaging: false,
              exportAllData: true,
              exportButton: false,
              pageSize: 10,
              pageSizeOptions: [5, 10, 30, 60],
              padding: "dense",
              searchFieldAlignment: "left",
              showTitle: false,
              maxBodyHeight: "600px",
              search: true,
              sorting: false,
            }}
          />
        </TableWrapper>
      </Panel>
    </>
  );
};

export default DischargeMonitoringReport;
